<script setup lang="ts">
import {
  ArrowDown,
  ArrowUp,
  CirclePlus,
  InfoFilled,
  Lock,
  Unlock,
} from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref, watch } from "vue";
import {
  ChecklistListDto,
  ChecklistsInWorkflowDefinition,
  EntityFormInWorkflowDefinition,
  FormDefinition,
  FormRule,
  FormType,
  OrgGroupDto,
  WorkflowDefinition,
  WorkflowDefinitionListDto,
} from "~/api";
import type { FormElementWithAnswerCountDto } from "~/api/generated/models/FormElementWithAnswerCountDto";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth, emptyGuid } from "~/utils/constants";
import { nonAnswerableTypes } from "~/utils/formUtils";
import { showError } from "~/utils/handleAPIError";
const myInfoStore = useMyInfoStore();

import { useStorage } from "@vueuse/core";
import {
  buildVersionInfo,
  getFormVersionKey,
  type VersionFamily,
} from "~/NewFormBuilder/composables/useFormVersions";
import FormDesigner from "~/NewFormBuilder/components/Designer/FormDesigner.vue";
import FormVersionGroupTable from "./FormVersionGroupTable.vue";
import ListsTab from "~/NewFormBuilder/components/Designer/FormEditors/ListsTab.vue";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { Slotted } from "~/types/Slotted";
import { formatDate } from "~/utils/dateUtils";
import EmailTemplateList from "../pages/EmailTemplateList.vue";
import ShortcodeList from "../pages/ShortcodeList.vue";
import TimeFrames from "./TimeFrames.vue";
import WorkflowDefinitionChangeEvents from "./WorkflowDefinitionChangeEvents.vue";
import WorkflowDefinitionListing from "./WorkflowDefinitionListing.vue";
import WorkflowDefinitionStatuses from "./WorkflowDefinitionStatuses.vue";
const entityDefinitionStore = useEntityDefinitionStore();

type mainType = WorkflowDefinition;
type listItemType = WorkflowDefinitionListDto;

const drawerOpen = defineModel<boolean>({ required: true });
const props = defineProps<{
  workflowDefinitionListDto: listItemType;
}>();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updated", value: mainType): void;
  (e: "added", value: mainType): void;
  (e: "deleted", value: mainType): void;
}>();

const workflowDefinition = ref<mainType>({
  guid: "",
  sysOrgGuid: "",
  name: "",
  statusList: [],
  timeFrames: [],
  checklistsInWorkflowDefinitions: [],
  closeDrawerOnStatuses: [],
  questionsToShow: [],
  statusChangeActionsList: [],
});
const lastSavedJson = ref<string>("");
const lastSavedFormJson = ref<string>("");

const activeWorkflowTab = useStorage(
  "activeWorkflowTab",
  "general",
  sessionStorage,
);

/** edit history for the user */
// const editHistory = useRefHistory(workflowDefinition, { deep: true });\

const designerDrawerOpen = ref<boolean>(false);
const formBeingEdited = ref<FormDefinition>();
const onlyShowItemsThatNeedAttention = ref<boolean>(false);
const availableChecklists = ref<ChecklistListDto[]>([]);
const checklistsInWorkflowDefinition = ref<string[]>([]);
const groupsWithAllAccess = ref<string[]>([]);
const orphanedForms = ref<FormDefinition[]>([]);
const selectedOrphanedFormGuid = ref<string>("");
const adoptingForm = ref(false);
const syncing = ref(false);

const moveAnswerDialogVisible = ref(false);
const moveAnswerStep = ref<"source" | "target" | "confirm">("source");
const elementsWithAnswers = ref<FormElementWithAnswerCountDto[]>([]);
const selectedSourceElement = ref<FormElementWithAnswerCountDto | null>(null);
const selectedTargetElementGuid = ref<string>("");
const movingAnswers = ref(false);

const isUsable = computed(() => {
  return (
    workflowDefinition.value.guid !== emptyGuid &&
    workflowDefinition.value.guid !== undefined
  );
});
const editing = ref(true);
const hasSetupBasicRole = myInfoStore.hasSetupBasicRole;
const isDevelopment = import.meta.env.DEV;

watch(
  () => workflowDefinition.value, // trigger when loaded
  () => {
    editing.value = !workflowDefinition.value?.guid;
  },
);

const workflowStepForms = computed<FormDefinition[]>(
  () =>
    workflowDefinition.value.formDefinitions?.filter(
      (f) => f.formType === FormType.WORKFLOW_STEP,
    ) ?? [],
);

const generalForms = computed<FormDefinition[]>(
  () =>
    workflowDefinition.value.formDefinitions?.filter(
      (f) => f.formType === FormType.WORKFLOW_DEFINITION_MAIN_FORM,
    ) ?? [],
);

const legacyForms = computed<FormDefinition[]>(
  () =>
    workflowDefinition.value.formDefinitions?.filter(
      (f) =>
        f.formType !== FormType.WORKFLOW_STEP &&
        f.formType !== FormType.WORKFLOW_DEFINITION_MAIN_FORM,
    ) ?? [],
);

/**
 * Per-form version metadata (version number, active/default/latest, siblings) computed once
 * across all form definitions in the workflow. Keyed by form guid.
 */
const versionInfoByGuid = computed(() =>
  buildVersionInfo(workflowDefinition.value?.formDefinitions ?? []),
);

/**
 * Collapses a flat list of forms into version families — one entry per version group, keyed by
 * the shared template. Each family exposes its active version (the row shown collapsed) and all
 * its versions (shown when expanded). Family order follows the active version's position in the
 * source list, so workflow-step ordering is preserved.
 */
function toFamilies(forms: FormDefinition[]): VersionFamily[] {
  const byKey = new Map<string, FormDefinition[]>();
  for (const fd of forms) {
    const key = getFormVersionKey(fd);
    const list = byKey.get(key);
    if (list) list.push(fd);
    else byKey.set(key, [fd]);
  }

  const families: VersionFamily[] = [];
  for (const [key, versions] of byKey) {
    const active =
      versions.find((v) => versionInfoByGuid.value.get(v.guid)?.isActive) ??
      versions[0];
    families.push({ key, active, versions });
  }
  return families;
}

const workflowStepFamilies = computed(() =>
  toFamilies(workflowStepForms.value),
);
const generalFormFamilies = computed(() => toFamilies(generalForms.value));

// const handleDeleteItemDefinition = async (workflowDefinition: string) => {
//   if (!workflowDefinition) {
//     return showError("Compliance item not found");
//   }
//   ElMessageBox.confirm(
//     "This will permanently delete this compliance item. Continue?",
//     `Deleting Compliance Item`,
//     {
//       confirmButtonText: "OK",
//       cancelButtonText: "Cancel",
//       type: "warning",
//     },
//   ).then(async () => {
//     if (workflowDefinition) {
//       await apiClient.workflowDefinition
//         .deleteWorkflowDefinition(workflowDefinition)
//         .then(() => {
//           ElMessage.success("Compliance Item Deleted Successfully.");
//         })
//         .catch(handleError);
//     }
//     deleteDialogOpen.value = false;
//   });
// };
// const handleEditItemDefinition = (formGuid: string, itemGuid: string) => {
//   // ???
//   router.push(
//     applicationRouteMap.Compliance.children.ItemDesigner.path
//       .replace(":formGuid", formGuid)
//       .replace(":itemGuid", itemGuid),
//   );
// };

const rowIndexReordered = ref<number | undefined>(undefined);

const orderChanged = ref(false);

/**
 * Reorders whole version families among the workflow steps. All versions of a family move
 * together (order is a family concern, not per-version). Rebuilds formDefinitions preserving the
 * relative order of each family's versions.
 */
const handleChangeFamilyOrder = (family: VersionFamily, change: number) => {
  const families = workflowStepFamilies.value;
  const oldIndex = families.findIndex((f) => f === family);
  if (oldIndex === -1) {
    showError("Developer Error: tried to reorder non-existent workflow step");
    return;
  }
  let newIndex = oldIndex + change;
  if (newIndex < 0) newIndex = 0;
  else if (newIndex >= families.length) newIndex = families.length - 1;
  if (newIndex === oldIndex) return;

  const reordered = [...families];
  reordered.splice(oldIndex, 1);
  reordered.splice(newIndex, 0, family);
  orderChanged.value = true;

  workflowDefinition.value.formDefinitions = [
    ...generalForms.value,
    ...reordered.flatMap((f) => f.versions),
  ];
  setTimeout(() => (rowIndexReordered.value = undefined), 1000);
};

/**
 * Formats form rules for display, grouping duplicates and showing counts
 */
const getFormattedAccessRules = (formRules: FormRule[]) => {
  // Group rules by their display key (ruleType + roleGroupOrIndividual)
  const ruleCounts = new Map<string, number>();

  formRules.forEach((fr) => {
    const key = `${fr.ruleType} (${fr.roleGroupOrIndividual})`;
    ruleCounts.set(key, (ruleCounts.get(key) || 0) + 1);
  });

  // Convert to display strings with counts
  const displayRules = Array.from(ruleCounts.entries()).map(([key, count]) => {
    return count > 1 ? `${key} x ${count}` : key;
  });

  return displayRules.join(" ");
};

const updateWorkFlowStepOrder = async () => {
  try {
    const formsToUpdate = workflowStepForms.value.map((form) => form.guid);

    await apiClient.formDefinition
      .updateWorkflowStepOrder(workflowDefinition.value.guid, formsToUpdate)
      .then(() => {
        ElMessage.success("Saved");
        orderChanged.value = false;
        emit("updated", workflowDefinition.value);
        updateLastUpdated();
      })
      .catch(showError);
  } catch (error) {
    showError(error);
  }
};

const allSecurityGroups = ref<OrgGroupDto[]>([]);

const getSecurityGroups = async () => {
  await apiClient.orgGroup
    .getAllOrgGroupDtos(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      allSecurityGroups.value = res;
    })
    .catch(showError);
};

const chosenEntityDefinition = ref<string>();

const updateItemDefinition = async () => {
  if (!workflowDefinition.value) {
    return;
  }

  // ensure we have an OrgGuid
  if (!myInfoStore.currentSysOrgGuid) {
    showError("No current org guid");
    return;
  }
  workflowDefinition.value.sysOrgGuid = myInfoStore.currentSysOrgGuid;

  workflowDefinition.value.checklistsInWorkflowDefinitions =
    checklistsInWorkflowDefinition.value.map((guid, index) => {
      const cl: ChecklistsInWorkflowDefinition = {
        workflowDefinitionGuid: workflowDefinition.value.guid,
        checklistGuid: guid,
        sortOrder: index + 1,
      };

      return cl;
    });

  workflowDefinition.value.groupsWithAllAccess = groupsWithAllAccess.value;

  if (!workflowDefinition.value.guid) {
    // create if it doesnt exist already
    await apiClient.workflowDefinition
      .createWorkflowDefinition(workflowDefinition.value)
      .then((res) => {
        ElMessage.success("Saved");
        emit("added", res);
        // handleCloseDrawer();
      })
      .catch(showError);
  } else {
    await apiClient.workflowDefinition
      // otherwise, we just update it
      .updateWorkflowDefinition(
        workflowDefinition.value.guid,
        workflowDefinition.value,
      )
      .then((res) => {
        ElMessage.success("Saved");
        emit("updated", res);
      })
      .catch(showError);
  }

  updateLastUpdated();
};

const saveChanges = async () => {
  if (!canSave.value) {
    showError("Please fill in all required fields.");
    return;
  }
  if (!workflowDefinition.value.statusList.length) {
    showError("Please add at least one status.");
    return;
  }
  await updateItemDefinition();
  updateLastUpdated();
  editing.value = false;
};

const handleEditForm = async (form: FormDefinition) => {
  await apiClient.formDefinition
    .getFormDefinition(form.guid)
    .then((res) => {
      formBeingEdited.value = res;
      lastSavedFormJson.value = JSON.stringify(res);
      designerDrawerOpen.value = true;
    })
    .catch(showError);
};

const closeDrawer = () => {
  emit("closeDrawer");
};
const cancelEdit = () => {
  workflowDefinition.value = JSON.parse(lastSavedJson.value);
};
/**
 * Creates a new item definition then redirects to the edit page
 * for it.
 */
// const handleCreateItemDefinition = async () => {
//   ElMessageBox.prompt(
//     "Please enter the name of this compliance item:",
//     "Create New Compliance Item",
//     {
//       confirmButtonText: "Create",
//       cancelButtonText: "Cancel",
//     },
//   )
//     .then(async ({ value }) => {
//       const newItem: ComplianceItemDefinitionCreateDto = {
//         name: value,
//         type: ComplianceItemType.PERSON,
//         status: ComplianceItemStatus.ACTIVE,
//       };
//       await apiClient.complianceItemDefinition
//         .createComplianceItemDefinition(newItem)
//         .then((res) => {
//           itemDefinitionList.value.push(res);
//           handleEditItemDefinition(
//             res.formDefinitionGuid ?? "",
//             res.guid ?? "",
//           );
//         });
//     })
//     .catch(() => {
//       ElMessage({
//         type: "info",
//         message: "Canceled",
//       });
//     });
// };

const updateLastUpdated = () => {
  lastSavedJson.value = JSON.stringify(workflowDefinition.value);
};

/** 'true' if any changes have been made, and a save is needed */
const saveNeeded = computed(() => {
  const changes = [];

  // add issues to an array, to allow us to debug which changes are made
  if (JSON.stringify(workflowDefinition.value) !== lastSavedJson.value) {
    changes.push("workflowDefinition");
    if (isDevelopment) {
      console.log(
        "DEV: workflowDefinition changed",
        workflowDefinition.value,
        lastSavedJson.value,
      );
    }
  }
  if (saveNeededToForm.value) {
    changes.push("forms");
  }
  if (
    checklistsInWorkflowDefinition.value.join() !==
    workflowDefinition.value.checklistsInWorkflowDefinitions
      ?.map((cl) => cl.checklistGuid)
      .join()
  ) {
    changes.push("checklists");
  }

  // compare two arrays to see if they are both empty or both
  // contain the same elements
  if (
    (groupsWithAllAccess.value.join() ?? "") !==
    (workflowDefinition.value.groupsWithAllAccess?.join() ?? "")
  ) {
    console.log(
      "groups changed",
      groupsWithAllAccess.value.join(),
      workflowDefinition.value.groupsWithAllAccess?.join(),
    );
    changes.push("groups");
  }
  if (
    chosenEntityDefinition.value &&
    chosenEntityDefinition.value !==
      workflowDefinition.value.entityDefinitionGuid
  ) {
    changes.push("entityDefinition");
    if (isDevelopment) {
      console.log(
        "DEV: entityDefinition changed",
        chosenEntityDefinition.value,
        workflowDefinition.value.entityDefinitionGuid,
      );
    }
  }
  if (changes.length > 0) {
    console.log("Current changes:", changes);
  }
  return changes.length > 0;
});

/** 'true' if any changes have been made to the form being edited, and a save is needed */
const saveNeededToForm = ref(false);

const handleClose = async () => {
  if (saveNeeded.value) {
    ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    })
      // If the user confirms
      .then(() => {
        // do nothing
      })
      // If the user cancels
      .catch(() => {
        emit("closeDrawer");
      });
  } else {
    emit("closeDrawer");
  }
};

const handleCloseDesigner = async () => {
  if (saveNeededToForm.value) {
    ElMessageBox.confirm("You have unsaved form changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    })
      // If the user chooses to continue editing
      .then(() => {
        // do nothing
      })
      // If the user chooses to discard changes
      .catch(() => {
        formBeingEdited.value = undefined;
        saveNeededToForm.value = false;
        designerDrawerOpen.value = false;
      });
  } else {
    // If there are no unsaved changes, replace the form in the workflow definition
    // with the edited form
    if (formBeingEdited.value) {
      const index = workflowDefinition.value.formDefinitions?.findIndex(
        (f) => f.guid === formBeingEdited.value?.guid,
      );
      if (
        index !== undefined &&
        index !== -1 &&
        workflowDefinition.value.formDefinitions
      ) {
        workflowDefinition.value.formDefinitions[index] = formBeingEdited.value;
        updateLastUpdated();
      }
    }
    formBeingEdited.value = undefined;
    saveNeededToForm.value = false;
    designerDrawerOpen.value = false;
  }
};

const fetchWorkflowDefinition = async (guidWanted: string) => {
  apiClient.workflowDefinition
    .getWorkflowDefinition(guidWanted, myInfoStore.currentSysOrgGuid)
    .then((res) => {
      if (res.formDefinitions) {
        res.formDefinitions.sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
        );
      }
      workflowDefinition.value = res;
      chosenEntityDefinition.value =
        workflowDefinition.value?.entityDefinitionGuid ?? undefined;
      updateLastUpdated();
      checklistsInWorkflowDefinition.value =
        workflowDefinition.value.checklistsInWorkflowDefinitions?.map(
          (cl) => cl.checklistGuid,
        );
      groupsWithAllAccess.value =
        workflowDefinition.value.groupsWithAllAccess ?? [];
      // Also load entity forms for this workflow
      entityFormsInWorkflow.value =
        workflowDefinition.value.entityFormsInWorkflowDefinition ?? [];
    });
};

const getAvailableChecklists = async () => {
  await apiClient.checklistDefinition
    .getAllChecklistDefinitions(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      availableChecklists.value = res.filter(
        (checklist) =>
          checklist.entityDefinitionGuid === chosenEntityDefinition.value ||
          checklist.entityDefinitionGuid === parentEntityDefinitionGuid.value,
      );
    })
    .catch(showError);
};

const parentEntityDefinitionGuid = computed(() => {
  const filteredEntity = entityDefinitionStore.EntitiesDefinitionList.find(
    (entity) => entity.guid === chosenEntityDefinition.value,
  );
  return filteredEntity?.parentEntityDefinitionGuid;
});

const handleArchiveWorkflow = async () => {
  if (!workflowDefinition.value) {
    showError("Workflow not found");
    return;
  }
  ElMessageBox.confirm(
    "This will archive this workflow. Continue?",
    `Archiving Workflow`,
    {
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    },
  ).then(async () => {
    if (workflowDefinition.value) {
      workflowDefinition.value.archivedAt = new Date().toISOString();
      await updateItemDefinition();
    }
    closeDrawer();
  });
};

const handleCreateForm = async (isWorkflowStep: boolean) => {
  // message box with input for form name
  ElMessageBox.prompt("Please enter a name for the form", "Form Name", {
    confirmButtonText: "Create",
    cancelButtonText: "Cancel",
  })
    .then(({ value: formName }) => {
      // create form
      apiClient.workflowDefinition
        .createWorkflowDefinitionForm(
          workflowDefinition.value.guid,
          formName,
          isWorkflowStep,
        )
        .then((res) => {
          ElMessage.success("Form added successfully");
          workflowDefinition.value.formDefinitions?.push(res);
          updateLastUpdated();
          // open form designer
          handleEditForm(res);
        })
        .catch(showError);
    })
    .catch(() => {
      ElMessage({
        type: "info",
        message: "Canceled",
      });
    });
};

const makeFormDefaultForItsCode = async (formRow: FormDefinition) => {
  if (!formRow?.guid) return;
  try {
    // A dedicated endpoint, not a full-form save: the default pointer is not form content,
    // so this must work for in-use forms too (a whole-form PUT is rejected for those).
    await apiClient.formDefinition.makeDefaultVersion(formRow.guid);
    ElMessage.success("Marked as default version for its FormCode group");
    await fetchWorkflowDefinition(workflowDefinition.value.guid);
  } catch (e) {
    showError(e);
  }
};

const fetchOrphanedForms = async () => {
  if (!myInfoStore.currentSysOrgGuid) {
    return;
  }
  await apiClient.workflowDefinition
    .getOrphanedFormDefinitions(myInfoStore.currentSysOrgGuid)
    .then((res) => {
      orphanedForms.value = res;
    })
    .catch(showError);
};

const handleAdoptOrphanedForm = async () => {
  if (!selectedOrphanedFormGuid.value || !workflowDefinition.value.guid) {
    return;
  }

  adoptingForm.value = true;
  try {
    const result = await apiClient.workflowDefinition.adoptFormIntoWorkflow(
      workflowDefinition.value.guid,
      selectedOrphanedFormGuid.value,
    );

    let message = "Form adopted into workflow successfully.";
    if ((result.workflowsCreated ?? 0) > 0) {
      message += ` Migrated ${result.formResponsesMigrated} response(s) into ${result.workflowsCreated} new workflow(s).`;
    }
    if ((result.skippedNoEntity ?? 0) > 0) {
      message += ` ${result.skippedNoEntity} response(s) skipped (no linked entity).`;
    }
    ElMessage.success(message);

    if (result.errors?.length) {
      ElMessage.warning(
        `${result.errors.length} error(s) during migration. Check logs.`,
      );
    }

    selectedOrphanedFormGuid.value = "";
    fetchWorkflowDefinition(workflowDefinition.value.guid);
    fetchOrphanedForms();
  } catch (e) {
    showError(e);
  } finally {
    adoptingForm.value = false;
  }
};

const handleSyncFormsToWorkflows = async () => {
  if (!workflowDefinition.value.guid) return;

  syncing.value = true;
  try {
    const preview = await apiClient.workflowDefinition.syncFormsToWorkflows(
      workflowDefinition.value.guid,
      true,
    );

    if (preview.orphanedFormResponsesFound === 0) {
      ElMessage.info("All form responses already have workflow instances.");
      return;
    }

    try {
      await ElMessageBox.confirm(
        `Found ${preview.orphanedFormResponsesFound} orphaned form response(s). ` +
          `${preview.formResponsesMigrated} will be migrated into new workflows` +
          (preview.skippedNoEntity
            ? `, ${preview.skippedNoEntity} skipped (no linked entity)`
            : "") +
          ".",
        "Sync Forms to Workflows",
        {
          confirmButtonText: "Execute",
          cancelButtonText: "Cancel",
          type: "warning",
        },
      );
    } catch {
      return;
    }

    const result = await apiClient.workflowDefinition.syncFormsToWorkflows(
      workflowDefinition.value.guid,
      false,
    );

    ElMessage.success(
      `Migrated ${result.formResponsesMigrated} response(s) into ${result.workflowsCreated} new workflow(s).`,
    );

    if (result.errors?.length) {
      ElMessage.warning(
        `${result.errors.length} error(s) during sync. Check logs.`,
      );
    }
  } catch (e) {
    showError(e);
  } finally {
    syncing.value = false;
  }
};

// --- Move Answer ---
const targetElementCandidates = computed(() => {
  if (!selectedSourceElement.value) return [];
  const sourceFormGuid = selectedSourceElement.value.formDefinitionGuid;
  return (
    workflowDefinition.value.formDefinitions
      ?.filter((fd) => fd.guid !== sourceFormGuid)
      .flatMap(
        (fd) =>
          fd.components
            ?.filter((c) => !nonAnswerableTypes.includes(c.type))
            .map((c) => ({
              formElementGuid: c.guid,
              label: c.label ?? c.questionCode,
              questionCode: c.questionCode,
              formElementType: c.type,
              formDefinitionGuid: fd.guid,
              formDefinitionName: fd.name,
            })) ?? [],
      ) ?? []
  );
});

const handleOpenMoveAnswer = async () => {
  moveAnswerStep.value = "source";
  selectedSourceElement.value = null;
  selectedTargetElementGuid.value = "";
  movingAnswers.value = false;

  try {
    elementsWithAnswers.value =
      await apiClient.workflowDefinition.getFormElementsWithAnswers(
        workflowDefinition.value.guid,
      );
    moveAnswerDialogVisible.value = true;
  } catch (e) {
    showError(e);
  }
};

const handleSelectSource = (row: FormElementWithAnswerCountDto) => {
  selectedSourceElement.value = row;
  selectedTargetElementGuid.value = "";
  moveAnswerStep.value = "target";
};

const handleExecuteMove = async () => {
  if (!selectedSourceElement.value || !selectedTargetElementGuid.value) return;

  const targetCandidate = targetElementCandidates.value.find(
    (c) => c.formElementGuid === selectedTargetElementGuid.value,
  );

  try {
    await ElMessageBox.confirm(
      `Move ${selectedSourceElement.value.answerCount} answer(s) from "${selectedSourceElement.value.label}" (${selectedSourceElement.value.formDefinitionName}) to "${targetCandidate?.label}" (${targetCandidate?.formDefinitionName})? This cannot be undone.`,
      "Confirm Move",
      {
        confirmButtonText: "Move",
        cancelButtonText: "Cancel",
        type: "warning",
      },
    );
  } catch {
    return;
  }

  movingAnswers.value = true;
  try {
    const result = await apiClient.workflowDefinition.moveAnswers(
      workflowDefinition.value.guid,
      {
        sourceElementGuid: selectedSourceElement.value.formElementGuid!,
        targetElementGuid: selectedTargetElementGuid.value,
      },
    );

    let message = `Moved ${result.answersMoved ?? 0} of ${result.totalAnswersFound ?? 0} answer(s).`;
    if ((result.skippedDuplicateConflict ?? 0) > 0) {
      message += ` ${result.skippedDuplicateConflict} skipped (already exists at target).`;
    }
    if ((result.skippedNoTargetFormResponse ?? 0) > 0) {
      message += ` ${result.skippedNoTargetFormResponse} skipped (no workflow link).`;
    }
    if ((result.targetFormResponsesCreated ?? 0) > 0) {
      message += ` ${result.targetFormResponsesCreated} new form response(s) created.`;
    }
    ElMessage.success(message);

    if (result.errors?.length) {
      ElMessage.warning(
        `${result.errors.length} error(s) during move. Check logs.`,
      );
    }

    moveAnswerDialogVisible.value = false;
  } catch (e) {
    showError(e);
  } finally {
    movingAnswers.value = false;
  }
};
// --- End Move Answer ---

// --- Entity Forms in Workflow ---
const entityFormsInWorkflow = ref<EntityFormInWorkflowDefinition[]>([]);
const addingEntityForm = ref(false);
const selectedEntityDefinitionGuidForEntityForm = ref<string>("");
const newEntityFormLabel = ref<string>("");
const editingEntityForm = ref<EntityFormInWorkflowDefinition | null>(null);
const editingEntityFormDialogVisible = ref(false);

const entityDefinitionsWithForms = computed(() => {
  return entityDefinitionStore.EntitiesDefinitionList;
});

const fetchEntityFormsInWorkflow = async () => {
  if (!workflowDefinition.value?.guid) {
    return;
  }
  try {
    entityFormsInWorkflow.value =
      await apiClient.workflowDefinition.getEntityFormsInWorkflowDefinition(
        workflowDefinition.value.guid,
      );
  } catch (error) {
    showError(error);
  }
};

const handleAddEntityForm = async () => {
  if (
    !selectedEntityDefinitionGuidForEntityForm.value ||
    !workflowDefinition.value?.guid
  ) {
    return;
  }
  addingEntityForm.value = true;
  try {
    const config: Partial<EntityFormInWorkflowDefinition> = {};
    if (newEntityFormLabel.value.trim()) {
      config.label = newEntityFormLabel.value.trim();
      config.linkType = newEntityFormLabel.value.trim();
    }
    const result =
      await apiClient.workflowDefinition.addEntityFormToWorkflowDefinition(
        workflowDefinition.value.guid,
        selectedEntityDefinitionGuidForEntityForm.value,
        config as EntityFormInWorkflowDefinition,
      );
    entityFormsInWorkflow.value.push(result);
    selectedEntityDefinitionGuidForEntityForm.value = "";
    newEntityFormLabel.value = "";
    ElMessage.success("Entity form added to workflow");
  } catch (error) {
    showError(error);
  } finally {
    addingEntityForm.value = false;
  }
};

const handleUpdateEntityForm = async (
  entityForm: EntityFormInWorkflowDefinition,
) => {
  if (!entityForm.guid) {
    return;
  }
  try {
    const result =
      await apiClient.workflowDefinition.updateEntityFormInWorkflowDefinition(
        entityForm.guid,
        entityForm,
      );
    const index = entityFormsInWorkflow.value.findIndex(
      (ef) => ef.guid === entityForm.guid,
    );
    if (index !== -1) {
      entityFormsInWorkflow.value[index] = result;
    }
    editingEntityForm.value = null;
    editingEntityFormDialogVisible.value = false;
    ElMessage.success("Entity form configuration updated");
  } catch (error) {
    showError(error);
  }
};

const handleRemoveEntityForm = async (
  entityForm: EntityFormInWorkflowDefinition,
) => {
  if (!entityForm.guid) {
    return;
  }

  try {
    await ElMessageBox.confirm(
      `Remove the entity form "${entityForm.entityDefinition?.name ?? "Unknown"}" from this workflow?`,
      "Remove Entity Form",
      {
        confirmButtonText: "Remove",
        cancelButtonText: "Cancel",
        type: "warning",
      },
    );
  } catch {
    return; // User cancelled
  }

  try {
    await apiClient.workflowDefinition.removeEntityFormFromWorkflowDefinition(
      entityForm.guid,
    );
    entityFormsInWorkflow.value = entityFormsInWorkflow.value.filter(
      (ef) => ef.guid !== entityForm.guid,
    );
    ElMessage.success("Entity form removed from workflow");
  } catch (error) {
    showError(error);
  }
};

const handleEntityFormOrderChange = (
  entityForm: EntityFormInWorkflowDefinition,
  change: number,
) => {
  const index = entityFormsInWorkflow.value.indexOf(entityForm);
  if (index === -1) {
    return;
  }

  let newIndex = index + change;
  if (newIndex < 0) {
    newIndex = 0;
  }
  if (newIndex >= entityFormsInWorkflow.value.length) {
    newIndex = entityFormsInWorkflow.value.length - 1;
  }

  entityFormsInWorkflow.value.splice(index, 1);
  entityFormsInWorkflow.value.splice(newIndex, 0, entityForm);

  // Update sort orders
  entityFormsInWorkflow.value.forEach((ef, i) => {
    ef.sortOrder = i;
  });
};

const handleSaveEntityFormOrder = async () => {
  try {
    for (const ef of entityFormsInWorkflow.value) {
      if (ef.guid) {
        await apiClient.workflowDefinition.updateEntityFormInWorkflowDefinition(
          ef.guid,
          ef,
        );
      }
    }
    ElMessage.success("Entity form order saved");
  } catch (error) {
    showError(error);
  }
};

const startEditEntityForm = (entityForm: EntityFormInWorkflowDefinition) => {
  editingEntityForm.value = { ...entityForm };
  editingEntityFormDialogVisible.value = true;
};

const cancelEditEntityForm = () => {
  editingEntityForm.value = null;
  editingEntityFormDialogVisible.value = false;
};
// --- End Entity Forms in Workflow ---

/** 'true' if the compliance item has all fields filled in */
const canSave = computed(
  () => !!workflowDefinition.value?.name,
  //  &&
  // itemDefinition.value?.type &&
  // itemDefinition.value?.status,
);

onMounted(async () => {
  await Promise.all([
    getAvailableChecklists(),
    entityDefinitionStore.load(),
    getSecurityGroups(),
    fetchOrphanedForms(),
    fetchEntityFormsInWorkflow(),
  ]);

  const guidWanted = props.workflowDefinitionListDto.guid;
  if (guidWanted?.startsWith("new") || !guidWanted) {
    activeWorkflowTab.value = "general";
    workflowDefinition.value = {
      // @ts-expect-error - want the guid to be set by server
      guid: undefined,
      name: "",
      statusList: [
        { code: "Started", label: "Started", isEndpoint: false },
        { code: "In Progress", label: "In Progress", isEndpoint: false },
        { code: "Completed", label: "Completed", isEndpoint: true },
      ],
      timeFrames: [
        {
          code: "Default",
          label: "Default",
          startDate: undefined,
          endDate: undefined,
        },
      ],
    };
    updateLastUpdated();
  } else {
    fetchWorkflowDefinition(guidWanted);
  }
});

watch(
  () => chosenEntityDefinition.value,
  async (newGuid) => {
    if (!newGuid) {
      checklistsInWorkflowDefinition.value = [];
      availableChecklists.value = [];
    } else {
      await getAvailableChecklists();
    }

    workflowDefinition.value.entityDefinitionGuid =
      chosenEntityDefinition.value;
  },
);
</script>

<template>
  <el-drawer
    v-if="workflowDefinition"
    :model-value="drawerOpen"
    :before-close="handleClose"
    :destroy-on-close="true"
    :title="workflowDefinition.name || '\u00A0'"
    direction="rtl"
    :size="drawerWidth"
    class="workflow-definition-editor"
  >
    <el-tabs v-model="activeWorkflowTab" type="card" class="demo-tabs">
      <el-tab-pane label="Workflow Properties" name="general" lazy>
        <div v-if="hasSetupBasicRole" class="edit-buttons">
          <el-button v-if="!editing" type="success" @click="editing = true">
            Edit
          </el-button>

          <el-button v-if="editing" plain type="danger" @click="cancelEdit"
            >Cancel</el-button
          >
          <el-button
            v-if="editing"
            type="success"
            :disabled="!saveNeeded || !canSave"
            @click="saveChanges"
            >Save</el-button
          >
        </div>

        <div
          v-if="
            workflowDefinition.guid || workflowDefinition.guid === undefined
          "
          class="compliance-workflow-details"
        >
          <table class="section core">
            <tbody>
              <tr>
                <td>Name</td>
                <td>
                  <el-input v-if="editing" v-model="workflowDefinition.name" />
                  <span v-else>{{ workflowDefinition.name }}</span>
                </td>
              </tr>
              <tr>
                <td>Entity Type</td>
                <td>
                  <el-select
                    v-if="editing && !workflowDefinition.entityDefinitionGuid"
                    v-model="chosenEntityDefinition"
                    filterable
                    clearable
                  >
                    <el-option
                      v-for="entity in entityDefinitionStore.EntitiesDefinitionList"
                      :key="entity.guid"
                      :label="entity.name ?? undefined"
                      :value="entity.guid"
                    />
                  </el-select>
                  <span v-else>{{
                    entityDefinitionStore.nameOfType(
                      workflowDefinition.entityDefinitionGuid ?? undefined,
                    )
                  }}</span>
                </td>
              </tr>
              <tr>
                <td>Checklists</td>
                <td>
                  <div v-if="!editing">
                    <div
                      v-if="checklistsInWorkflowDefinition.length"
                      class="checklist-tags"
                    >
                      <el-tag
                        v-for="checklist in checklistsInWorkflowDefinition"
                        :key="checklist"
                      >
                        {{
                          availableChecklists.find((i) => i.guid === checklist)
                            ?.name || "Unknown Checklist"
                        }}
                      </el-tag>
                    </div>
                    <div v-else>-</div>
                  </div>
                  <div v-else>
                    <el-transfer
                      v-model="checklistsInWorkflowDefinition"
                      :data="availableChecklists"
                      :titles="['Available', 'Added']"
                      :filterable="true"
                      :props="{
                        key: 'guid',
                        label: 'name',
                      }"
                      :filter-placeholder="'Search'"
                      :disabled="!editing"
                    ></el-transfer>
                  </div>
                </td>
              </tr>
              <tr>
                <td>Status List</td>
                <td>
                  <div v-if="!editing" class="statusList">
                    <el-tag
                      v-for="status in workflowDefinition.statusList"
                      :key="status.code ?? ''"
                    >
                      {{ status.label }}
                      <i
                        v-if="status.isEndpoint"
                        :class="`mdi mdi-check-decagram`"
                      ></i>
                    </el-tag>
                  </div>
                  <div v-else>
                    <WorkflowDefinitionStatuses
                      :key="workflowDefinition.statusList?.length"
                      :workflow-definition="workflowDefinition"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>Time Frames</td>
                <td>
                  <div v-if="!editing" class="timeFrameList">
                    <div
                      v-for="timeFrame in workflowDefinition.timeFrames"
                      :key="timeFrame.code ?? ''"
                    >
                      {{ timeFrame.label }}:
                      {{ timeFrame.startDate ? " - From " : ""
                      }}<span>{{
                        timeFrame.startDate
                          ? formatDate(timeFrame.startDate, 7)
                          : "No start"
                      }}</span>
                      to
                      <span>{{
                        timeFrame.endDate
                          ? formatDate(timeFrame.endDate, 7)
                          : "No end"
                      }}</span>
                    </div>
                  </div>
                  <div v-else>
                    <TimeFrames
                      :key="workflowDefinition.timeFrames?.length"
                      :workflow-definition="workflowDefinition"
                    />
                  </div>
                </td>
              </tr>
              <tr>
                <td>Only Show Items That Need Attention</td>
                <td>
                  <el-checkbox
                    v-if="editing"
                    v-model="onlyShowItemsThatNeedAttention"
                    :active-action-icon="Unlock"
                    :inactive-action-icon="Lock"
                  />
                  <span v-else>
                    <el-icon v-if="onlyShowItemsThatNeedAttention"
                      ><el-icon-check
                    /></el-icon>
                    <el-icon v-else><el-icon-close /></el-icon>
                  </span>
                </td>
              </tr>
              <tr>
                <td>Groups with Access to all Responses</td>
                <td>
                  <div v-if="editing">
                    <el-select
                      v-model="groupsWithAllAccess"
                      placeholder="Select groups"
                      clearable
                      :multiple="true"
                    >
                      <el-option
                        v-for="group in allSecurityGroups"
                        :key="group.guid"
                        :label="group.name ?? group.guid ?? ''"
                        :value="group.guid"
                      ></el-option>
                    </el-select>
                  </div>
                  <div v-if="!editing && groupsWithAllAccess.length">
                    <el-tag v-for="group in groupsWithAllAccess" :key="group">
                      {{
                        allSecurityGroups.find((g) => g.guid === group)?.name ||
                        "Unknown Group"
                      }}
                    </el-tag>
                  </div>
                  <div v-else-if="!editing">-</div>
                </td>
              </tr>

              <tr>
                <td>Cancel Button on Status</td>
                <td>
                  <el-select
                    v-if="editing"
                    v-model="workflowDefinition.cancelableOnStatus"
                    placeholder="None"
                    clearable
                  >
                    <el-option
                      v-for="status in workflowDefinition.statusList.filter(
                        (s) => s.code != null,
                      )"
                      :key="status.code!"
                      :label="status.label ?? status.code!"
                      :value="status.code!"
                    />
                  </el-select>
                  <span v-else>{{
                    workflowDefinition.cancelableOnStatus
                      ? (workflowDefinition.statusList?.find(
                          (s) =>
                            s.code === workflowDefinition.cancelableOnStatus,
                        )?.label ?? workflowDefinition.cancelableOnStatus)
                      : "-"
                  }}</span>
                </td>
              </tr>

              <tr>
                <td>Enable Downloading</td>
                <td>
                  <el-checkbox
                    v-if="editing"
                    v-model="workflowDefinition.enableDownloading"
                  />
                  <span v-else>
                    <el-icon v-if="workflowDefinition.enableDownloading"
                      ><el-icon-check
                    /></el-icon>
                    <el-icon v-else><el-icon-close /></el-icon>
                  </span>
                </td>
              </tr>

              <tr>
                <td>Close Drawer on Statuses</td>
                <td>
                  <el-select
                    v-if="editing"
                    v-model="workflowDefinition.closeDrawerOnStatuses"
                    placeholder="None"
                    multiple
                    clearable
                  >
                    <el-option
                      v-for="status in workflowDefinition.statusList.filter(
                        (s) => s.code != null,
                      )"
                      :key="status.code!"
                      :label="status.label ?? status.code!"
                      :value="status.code!"
                    />
                  </el-select>
                  <span v-else>{{
                    workflowDefinition.closeDrawerOnStatuses?.length
                      ? workflowDefinition.closeDrawerOnStatuses
                          .map(
                            (code) =>
                              workflowDefinition.statusList?.find(
                                (s) => s.code === code,
                              )?.label ?? code,
                          )
                          .join(", ")
                      : "-"
                  }}</span>
                </td>
              </tr>

              <tr v-if="isUsable && (editing || workflowDefinition.archivedAt)">
                <td>
                  Archive<span v-if="workflowDefinition.archivedAt">d at</span>
                </td>
                <td>
                  <span v-if="workflowDefinition.archivedAt">
                    {{ formatDate(workflowDefinition.archivedAt, 1) }}
                  </span>
                  <el-button
                    v-else
                    type="danger"
                    class="archive"
                    title="Some responses exist, so this workflow cannot be deleted."
                    @click="handleArchiveWorkflow"
                    >Archive</el-button
                  >
                </td>
              </tr>
            </tbody>
          </table>
          <span class="comment"> Guid: {{ workflowDefinition.guid }} </span>
        </div>
      </el-tab-pane>
      <el-tab-pane v-if="isUsable" label="Forms" name="design" lazy>
        <div class="workflow-forms">
          <!-- Single shared form designer drawer for all forms tables. Opened by handleEditForm. -->
          <el-drawer
            v-if="formBeingEdited"
            v-model="designerDrawerOpen"
            :title="
              workflowDefinition
                ? `${workflowDefinition.name} - ${formBeingEdited.name}`
                : formBeingEdited.name
            "
            :append-to-body="true"
            :before-close="handleCloseDesigner"
            :size="drawerWidth"
          >
            <div class="formContainer">
              <FormDesigner
                :key="formBeingEdited.guid"
                v-model="formBeingEdited"
                :workflow-definition="workflowDefinition"
                :drawer-closed="!designerDrawerOpen"
                @save-needed="($event: boolean) => (saveNeededToForm = $event)"
                @close-drawer="designerDrawerOpen = false"
                @deleted="fetchWorkflowDefinition(workflowDefinition.guid)"
                @duplicated="fetchWorkflowDefinition(workflowDefinition.guid)"
              />
            </div>
          </el-drawer>
          <div
            v-if="hasSetupBasicRole"
            style="display: flex; justify-content: flex-end; margin: 0 1em 1em"
          >
            <el-button :loading="syncing" @click="handleSyncFormsToWorkflows">
              Sync Forms to Workflows
            </el-button>
            <el-button @click="handleOpenMoveAnswer">
              Move an Answer
            </el-button>
          </div>
          <div class="workflow-design-header">
            <h3>Inline Forms</h3>
            <el-button
              v-if="hasSetupBasicRole"
              :icon="CirclePlus"
              title="Add New Form"
              class="addButton"
              @click="handleCreateForm(true)"
            >
              New Form</el-button
            >
          </div>
          <div class="workflow-list">
            <div class="workflow-steps-editor">
              <FormVersionGroupTable
                v-if="workflowDefinition.formDefinitions"
                :families="workflowStepFamilies"
                :version-info-by-guid="versionInfoByGuid"
                :has-setup-basic-role="hasSetupBasicRole"
                reorderable
                :order-changed="orderChanged"
                @open="handleEditForm"
                @change-order="handleChangeFamilyOrder"
                @save-order="updateWorkFlowStepOrder"
                @make-default="makeFormDefaultForItsCode"
              >
                <template #extra-columns>
                  <el-table-column label="Multiple?" width="100">
                    <template #default="{ row }: Slotted<VersionFamily>">
                      {{
                        row.active.metaInfo?.multipleInstancesAllowed
                          ? `Min: ${row.active.metaInfo?.minInstances} ${row.active.metaInfo?.maxInstances ? ` Max: ${row.active.metaInfo?.maxInstances}` : ""}`
                          : "-"
                      }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Expand for">
                    <template #default="{ row }: Slotted<VersionFamily>">
                      {{
                        row.active.metaInfo.displayOnStatus?.length
                          ? row.active.metaInfo.displayOnStatus?.join(", ")
                          : "(All)"
                      }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Show for Status">
                    <template #default="{ row }: Slotted<VersionFamily>">
                      {{
                        row.active.metaInfo?.showOnlyForWorkflowStatuses?.length
                          ? row.active.metaInfo?.showOnlyForWorkflowStatuses?.join(
                              ", ",
                            )
                          : "(All)"
                      }}
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="User Created"
                    width="110"
                    align="center"
                  >
                    <template #default="{ row }: Slotted<VersionFamily>">
                      {{ row.active.metaInfo?.userCreated ? "Yes" : "-" }}
                    </template>
                  </el-table-column>
                  <el-table-column label="Access Rules" width="250">
                    <template #default="{ row }: Slotted<VersionFamily>">
                      {{
                        row.active.formRules === null
                          ? "(unknown)"
                          : row.active.formRules.length
                            ? getFormattedAccessRules(row.active.formRules)
                            : "(None)"
                      }}
                    </template>
                  </el-table-column>
                </template>
              </FormVersionGroupTable>
            </div>
          </div>
          <div class="workflow-design-header">
            <h3>Standalone Forms</h3>
            <el-button
              v-if="hasSetupBasicRole"
              :icon="CirclePlus"
              title="Add Form"
              class="addButton"
              @click="handleCreateForm(false)"
            >
              New Form
            </el-button>
          </div>
          <div class="workflow-list">
            <FormVersionGroupTable
              v-if="workflowDefinition.formDefinitions"
              :families="generalFormFamilies"
              :version-info-by-guid="versionInfoByGuid"
              :has-setup-basic-role="hasSetupBasicRole"
              @open="handleEditForm"
              @make-default="makeFormDefaultForItsCode"
            >
              <template #extra-columns>
                <el-table-column label="Multiple?">
                  <template #default="{ row }: Slotted<VersionFamily>">
                    {{
                      row.active.metaInfo?.multipleInstancesAllowed
                        ? `Min: ${row.active.metaInfo?.minInstances} ${row.active.metaInfo?.maxInstances ? ` Max: ${row.active.metaInfo?.maxInstances}` : ""}`
                        : "-"
                    }}
                  </template>
                </el-table-column>
              </template>
            </FormVersionGroupTable>
          </div>

          <!-- This is to allow backwards compatibility for forms that didn't originally belong to a workflow -->
          <div class="workflow-design-header">
            <h3>Legacy Forms</h3>
            <el-tooltip
              effect="dark"
              content="These forms were created before workflow steps were introduced. They are not associated with any workflow step."
            >
              <el-icon class="ml-2" style="color: #409eff">
                <InfoFilled />
              </el-icon>
            </el-tooltip>
          </div>

          <!-- Adopt orphaned form into this workflow -->
          <div v-if="hasSetupBasicRole" class="adopt-form-section">
            <h4>Adopt an Existing Form</h4>
            <p class="adopt-description">
              Select a form that is not currently associated with any workflow
              to add it to this workflow.
            </p>
            <div class="adopt-form-controls">
              <el-select
                v-model="selectedOrphanedFormGuid"
                filterable
                clearable
                placeholder="Select an orphaned form..."
                style="flex: 1"
              >
                <el-option
                  v-for="form in orphanedForms"
                  :key="form.guid"
                  :label="`${form.name} (${form.guid.substring(0, 8)})`"
                  :value="form.guid"
                />
              </el-select>
              <el-button
                type="primary"
                :disabled="!selectedOrphanedFormGuid"
                :loading="adoptingForm"
                @click="handleAdoptOrphanedForm"
              >
                Adopt Form
              </el-button>
            </div>
            <el-alert
              type="info"
              :closable="false"
              show-icon
              class="adopt-migration-warning"
            >
              Adopting a form will also migrate any existing orphaned form
              responses into new workflow instances automatically.
            </el-alert>
          </div>

          <div class="workflow-list">
            <el-table
              v-if="workflowDefinition.formDefinitions"
              :data="legacyForms"
              style="width: 100%; overflow-x: auto"
            >
              <el-table-column prop="name" label="Name">
                <template #default="{ row }">
                  <el-link type="primary" @click="handleEditForm(row)">
                    {{ row.name }}
                  </el-link>
                  <el-tooltip
                    v-if="row.hasActiveDraft"
                    content="This form has a draft in progress. Opening the designer resumes it."
                  >
                    <el-tag type="warning" size="small">Draft</el-tag>
                  </el-tooltip>
                  <div v-if="row.formCode" class="formCode" title="Form Code">
                    {{ row.formCode }}
                  </div>
                  <span class="guidDisplay"
                    >({{ row.guid.substring(0, 8) }})</span
                  >
                </template>
              </el-table-column>
              <el-table-column label="Multiple?">
                <template #default="{ row }: Slotted<FormDefinition>">
                  {{
                    row.metaInfo?.multipleInstancesAllowed
                      ? `Min: ${row.metaInfo?.minInstances} ${row.metaInfo?.maxInstances ? ` Max: ${row.metaInfo?.maxInstances}` : ""}`
                      : "-"
                  }}
                </template>
              </el-table-column>
              <el-table-column prop="formType" label="Form Type" />
              <el-table-column prop="updatedAt" label="Updated">
                <template #default="{ row }">
                  {{ formatDate(row.createdAt, 1) }}
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane
        v-if="isUsable"
        label="Entity Forms"
        name="entity-forms"
        lazy
      >
        <div class="entity-forms-tab">
          <div class="entity-forms-header">
            <h3>Entity Forms in Workflow</h3>
            <p class="entity-forms-description">
              Add entity definition forms as steps in this workflow. When users
              fill out these forms during a workflow, new entities can be
              automatically created.
            </p>
          </div>

          <!-- Add Entity Form -->
          <div v-if="hasSetupBasicRole" class="add-entity-form-section">
            <h4>Add Entity Form</h4>
            <div class="add-entity-form-controls">
              <el-select
                v-model="selectedEntityDefinitionGuidForEntityForm"
                filterable
                clearable
                placeholder="Select an entity definition..."
                style="flex: 1"
              >
                <el-option
                  v-for="entity in entityDefinitionsWithForms"
                  :key="entity.guid"
                  :label="entity.name ?? entity.guid"
                  :value="entity.guid"
                />
              </el-select>
              <el-input
                v-model="newEntityFormLabel"
                placeholder="Label (e.g. Applicant, Co-Applicant)"
                style="flex: 1"
                clearable
              />
              <el-button
                type="primary"
                :disabled="!selectedEntityDefinitionGuidForEntityForm"
                :loading="addingEntityForm"
                @click="handleAddEntityForm"
              >
                Add Entity Form
              </el-button>
            </div>
          </div>

          <!-- Entity Forms Table -->
          <div class="entity-forms-list">
            <el-table
              :data="entityFormsInWorkflow"
              style="width: 100%"
              empty-text="No entity forms configured for this workflow"
            >
              <el-table-column width="100" label="Order" align="center">
                <template #header>
                  <el-button
                    v-if="entityFormsInWorkflow.length > 1"
                    type="primary"
                    size="small"
                    @click="handleSaveEntityFormOrder"
                  >
                    Save Order
                  </el-button>
                </template>
                <template #default="{ row }">
                  <div v-if="hasSetupBasicRole" class="order-arrows">
                    <el-button
                      :icon="ArrowUp"
                      size="small"
                      @click="handleEntityFormOrderChange(row, -1)"
                    />
                    <el-button
                      :icon="ArrowDown"
                      size="small"
                      @click="handleEntityFormOrderChange(row, 1)"
                    />
                  </div>
                </template>
              </el-table-column>

              <el-table-column
                prop="entityDefinition.name"
                label="Entity Type"
                min-width="150"
              >
                <template #default="{ row }">
                  <strong>{{ row.entityDefinition?.name ?? "Unknown" }}</strong>
                  <div class="guidDisplay">
                    {{ row.entityDefinitionGuid?.substring(0, 8) }}
                  </div>
                </template>
              </el-table-column>

              <el-table-column prop="label" label="Label" min-width="150">
                <template #default="{ row }">
                  {{ row.label || row.entityDefinition?.name || "-" }}
                </template>
              </el-table-column>

              <el-table-column
                label="Auto-Create Entity"
                width="150"
                align="center"
              >
                <template #default="{ row }">
                  <el-tag
                    :type="
                      row.autoCreateEntityOnSubmission ? 'success' : 'info'
                    "
                    size="small"
                  >
                    {{ row.autoCreateEntityOnSubmission ? "Yes" : "No" }}
                  </el-tag>
                </template>
              </el-table-column>

              <el-table-column
                label="Link to Workflow"
                width="150"
                align="center"
              >
                <template #default="{ row }">
                  <el-tag
                    :type="row.linkEntityToWorkflow ? 'success' : 'info'"
                    size="small"
                  >
                    {{ row.linkEntityToWorkflow ? "Yes" : "No" }}
                  </el-tag>
                </template>
              </el-table-column>

              <el-table-column prop="linkType" label="Link Type" width="130">
                <template #default="{ row }">
                  {{ row.linkType || "-" }}
                </template>
              </el-table-column>

              <el-table-column label="Name Pattern" min-width="180">
                <template #default="{ row }">
                  {{ row.defaultEntityNamePattern || "-" }}
                </template>
              </el-table-column>

              <el-table-column
                v-if="hasSetupBasicRole"
                label="Actions"
                width="160"
                align="center"
              >
                <template #default="{ row }">
                  <el-button
                    type="primary"
                    size="small"
                    @click="startEditEntityForm(row)"
                  >
                    Edit
                  </el-button>
                  <el-button
                    type="danger"
                    size="small"
                    @click="handleRemoveEntityForm(row)"
                  >
                    Remove
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- Edit Entity Form Dialog -->
          <el-dialog
            v-model="editingEntityFormDialogVisible"
            title="Edit Entity Form Configuration"
            width="500px"
            :close-on-click-modal="false"
          >
            <template v-if="editingEntityForm">
              <el-form label-width="180px">
                <el-form-item label="Entity Type">
                  <strong>{{
                    editingEntityForm.entityDefinition?.name ?? "Unknown"
                  }}</strong>
                </el-form-item>
                <el-form-item label="Label">
                  <el-input
                    v-model="editingEntityForm.label"
                    placeholder="e.g. Applicant, Co-Applicant, Emergency Contact"
                  />
                  <span class="form-help-text">
                    Distinguish multiple forms of the same entity type
                  </span>
                </el-form-item>
                <el-form-item label="Auto-Create Entity">
                  <el-switch
                    v-model="editingEntityForm.autoCreateEntityOnSubmission"
                  />
                  <span class="form-help-text">
                    Automatically create an entity when the form is submitted
                  </span>
                </el-form-item>
                <el-form-item label="Link to Workflow">
                  <el-switch v-model="editingEntityForm.linkEntityToWorkflow" />
                  <span class="form-help-text">
                    Link the created entity to the workflow instance
                  </span>
                </el-form-item>
                <el-form-item label="Link Type">
                  <el-input
                    v-model="editingEntityForm.linkType"
                    placeholder="e.g. Person, Organization"
                  />
                </el-form-item>
                <el-form-item label="Name Pattern">
                  <el-input
                    v-model="editingEntityForm.defaultEntityNamePattern"
                    placeholder="e.g. {FormField:Name}"
                  />
                  <span class="form-help-text">
                    Can use shortcodes like {FormField:Name}
                  </span>
                </el-form-item>
              </el-form>
            </template>
            <template #footer>
              <el-button @click="cancelEditEntityForm">Cancel</el-button>
              <el-button
                type="primary"
                @click="
                  editingEntityForm && handleUpdateEntityForm(editingEntityForm)
                "
              >
                Save
              </el-button>
            </template>
          </el-dialog>
        </div>
      </el-tab-pane>

      <el-tab-pane
        v-if="
          workflowDefinition.guid &&
          workflowDefinition.guid !== '000000-0000-0000-0000-0000000'
        "
        label="Lists"
        name="lists"
        lazy
      >
        <ListsTab
          v-if="workflowDefinition.guid"
          :workflow-definition-guid="workflowDefinition.guid"
          :readonly="!myInfoStore.hasSetupBasicRole"
        >
        </ListsTab>
      </el-tab-pane>
      <el-tab-pane v-if="isUsable" label="Change Events" name="events" lazy>
        <WorkflowDefinitionChangeEvents
          :workflow-definition="workflowDefinition"
          :read-only="!hasSetupBasicRole"
          @updated="
            (updatedDefinition: WorkflowDefinition) => {
              workflowDefinition = updatedDefinition;
              updateLastUpdated();
            }
          "
        />
      </el-tab-pane>
      <el-tab-pane v-if="isUsable" label="Display Columns" name="listing" lazy>
        <WorkflowDefinitionListing
          :workflow-definition="workflowDefinition"
          :read-only="!hasSetupBasicRole"
          @updated="
            (updatedDefinition: WorkflowDefinition) => {
              workflowDefinition = updatedDefinition;
              updateLastUpdated();
            }
          "
        />
      </el-tab-pane>
      <el-tab-pane
        v-if="isUsable"
        label="Email Templates"
        name="templates"
        lazy
      >
        <EmailTemplateList
          :workflow-definition-guid="workflowDefinition.guid"
        />
      </el-tab-pane>
      <el-tab-pane
        v-if="isUsable"
        label="Shortcodes"
        name="shortcode-list"
        lazy
      >
        <ShortcodeList :workflow-definition="workflowDefinition" />
      </el-tab-pane>
    </el-tabs>

    <!-- Move Answer Dialog -->
    <el-dialog
      v-model="moveAnswerDialogVisible"
      title="Move Answers Between Forms"
      width="1000px"
      :close-on-click-modal="false"
    >
      <!-- Step 1: Pick source element -->
      <div v-if="moveAnswerStep === 'source'">
        <p>Select the source element whose answers you want to move:</p>
        <el-table
          :data="elementsWithAnswers"
          highlight-current-row
          style="width: 100%"
          @row-click="handleSelectSource"
        >
          <el-table-column prop="formDefinitionName" label="Form" width="200" />
          <el-table-column prop="label" label="Question" />
          <el-table-column prop="questionCode" label="Code" width="300" />
          <el-table-column prop="formElementType" label="Type" width="150" />
          <el-table-column
            prop="answerCount"
            label="Answers"
            width="80"
            align="center"
          />
        </el-table>
      </div>

      <!-- Step 2: Pick target element -->
      <div v-if="moveAnswerStep === 'target' && selectedSourceElement">
        <el-button
          size="small"
          style="margin-bottom: 1em"
          @click="moveAnswerStep = 'source'"
        >
          Back
        </el-button>
        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="Source">
            {{ selectedSourceElement.label }} ({{
              selectedSourceElement.formDefinitionName
            }}) &mdash; {{ selectedSourceElement.answerCount }} answer(s)
          </el-descriptions-item>
        </el-descriptions>

        <p style="margin-top: 1em">
          Select the target element to move answers into:
        </p>
        <el-table
          :data="targetElementCandidates"
          highlight-current-row
          style="width: 100%"
          @row-click="
            (row: (typeof targetElementCandidates)[0]) => {
              selectedTargetElementGuid = row.formElementGuid;
              moveAnswerStep = 'confirm';
            }
          "
        >
          <el-table-column prop="formDefinitionName" label="Form" width="200" />
          <el-table-column prop="label" label="Question" width="200" />
          <el-table-column prop="questionCode" label="Code" width="300" />
          <el-table-column prop="formElementType" label="Type" width="150" />
        </el-table>
      </div>

      <!-- Step 3: Confirm -->
      <div v-if="moveAnswerStep === 'confirm' && selectedSourceElement">
        <el-button
          size="small"
          style="margin-bottom: 1em"
          @click="moveAnswerStep = 'target'"
        >
          Back
        </el-button>
        <el-alert
          type="warning"
          :closable="false"
          show-icon
          style="margin-bottom: 1em"
        >
          This will move
          {{ selectedSourceElement.answerCount }} answer(s) from "{{
            selectedSourceElement.label
          }}" to "{{
            targetElementCandidates.find(
              (c) => c.formElementGuid === selectedTargetElementGuid,
            )?.label
          }}". This cannot be undone.
        </el-alert>
        <el-button
          type="primary"
          :loading="movingAnswers"
          @click="handleExecuteMove"
        >
          Execute Move
        </el-button>
      </div>
    </el-dialog>
  </el-drawer>
</template>

<style lang="less">
.workflow-definition-editor {
  .el-tab-pane {
    overflow: auto;
  }

  #pane-general {
    overflow: hidden;
    .edit-buttons {
      margin-bottom: 1em;
    }

    .section {
      width: 100%;
      tr {
        td:first-child {
          width: 270px;
        }
      }
    }
  }

  .compliance-workflow-details {
    display: grid;
    grid-template-rows: repeat(2, max-content) auto;
    max-height: 100%;
    overflow-y: auto;
    .checklist-tags {
      display: flex;
      gap: 0.5em;
      flex-wrap: wrap;
    }

    .archive {
      align-self: end;
      justify-self: end;
    }
    .formOptions {
      display: flex;
      flex-direction: column;
      gap: 0.5em;
    }

    table {
      td {
        padding: 0.5em;
        border-bottom: 2px solid var(--el-border-color-darker);
      }
    }
  }

  .formCode {
    opacity: 0.8;
    font-style: italic;
    font-size: 0.85em;
  }

  .SaveOrderBtn {
    height: 2.5em;
  }
  .workflow-forms {
    display: flex;
    flex-direction: column;

    .workflow-design-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      margin: 0 1em;
      align-items: center;
    }

    .workflow-list {
      margin: 0 0 4em;
    }

    .guidDisplay {
      font-size: 0.8em;
      color: var(--el-color-primary-light-3);
      margin-left: 2em;

      white-space: nowrap;
      > span {
        margin-right: 0.25em;
      }
    }

    .el-table {
      flex: 1;
    }

    .addButton {
      margin-left: auto;
    }

    .adopt-form-section {
      margin: 0 1em 2em;
      padding: 1em;
      border: 1px solid var(--el-border-color);
      border-radius: 8px;
      background: var(--el-fill-color-lighter);

      h4 {
        margin: 0 0 0.5em;
      }

      .adopt-description {
        margin: 0 0 1em;
        color: var(--el-text-color-secondary);
        font-size: 0.9em;
      }

      .adopt-form-controls {
        display: flex;
        gap: 0.75em;
        align-items: center;
        margin-bottom: 1em;
      }

      .adopt-migration-warning {
        margin-top: 1em;

        .migration-pseudocode {
          margin-top: 0.5em;
          cursor: pointer;

          summary {
            font-weight: 600;
            margin-bottom: 0.5em;
          }

          pre {
            background: var(--el-fill-color-dark);
            padding: 0.75em;
            border-radius: 4px;
            font-size: 0.8em;
            line-height: 1.5;
            overflow-x: auto;
            white-space: pre-wrap;
          }
        }
      }
    }
  }

  .entity-forms-tab {
    padding: 0 1em;

    .entity-forms-header {
      margin-bottom: 1.5em;

      h3 {
        margin: 0 0 0.5em;
      }

      .entity-forms-description {
        color: var(--el-text-color-secondary);
        font-size: 0.9em;
        margin: 0;
      }
    }

    .add-entity-form-section {
      margin-bottom: 2em;
      padding: 1em;
      border: 1px solid var(--el-border-color);
      border-radius: 8px;
      background: var(--el-fill-color-lighter);

      h4 {
        margin: 0 0 0.75em;
      }

      .add-entity-form-controls {
        display: flex;
        gap: 0.75em;
        align-items: center;
      }
    }

    .entity-forms-list {
      margin-bottom: 2em;
    }

    .form-help-text {
      display: block;
      color: var(--el-text-color-secondary);
      font-size: 0.85em;
      margin-top: 0.25em;
    }
  }
  .statusList {
    .el-tag {
      display: flex;
      width: fit-content;
      margin: 2px 0;
    }
  }
  .timeFrameList {
    div {
      margin: 2px 0;
    }
    span {
      color: var(--el-color-warning);
      padding: 0 0.5em;
    }
  }
  .order-arrows {
    display: flex;
    flex-direction: column;
    align-items: center;
    .el-button {
      width: min-content;
      margin: 0;
    }
  }
  .defaultFormCode {
    color: #67c23a;
    font-weight: 600;
    font-style: normal;
  }
  .comment {
    margin: 1em 0 0 auto;
    color: #555;
    font-size: 0.8em;
  }
}
</style>
